import React from "react";
import { MENU_ITEMS } from "../constants/menuConfig";

const PURCHASE_MENU_KEYS = new Set(["purchase"]);
const INVENTORY_MENU_KEYS = new Set(["inventory"]);

const getExternalPurchaseBaseUrl = () => String(process.env.REACT_APP_PURCHASE_MODULE_URL || "").trim().replace(/\/$/, "");
const isExternalPurchaseNavEnabled = () => {
  const mode = String(process.env.REACT_APP_PURCHASE_MODULE_NAV_MODE || "").trim().toLowerCase();
  return mode === "external";
};

const getExternalInventoryBaseUrl = () => String(process.env.REACT_APP_INVENTORY_MODULE_URL || "").trim().replace(/\/$/, "");
const isExternalInventoryNavEnabled = () => {
  const mode = String(process.env.REACT_APP_INVENTORY_MODULE_NAV_MODE || "").trim().toLowerCase();
  return mode === "external";
};

const getMenuHref = key => `#${key}`;

const buildExternalPurchaseUrl = () => {
  const purchaseModuleUrl = getExternalPurchaseBaseUrl();
  if (!purchaseModuleUrl || !isExternalPurchaseNavEnabled()) return "";

  let theme = "dark";
  let token = "";
  if (typeof window !== "undefined") {
    theme = String(localStorage.getItem("dw-theme") || "dark").trim().toLowerCase() || "dark";
    token = String(localStorage.getItem("dw-auth-token") || "").trim();
  }

  const query = new URLSearchParams();
  query.set("token", token);
  query.set("theme", theme);
  return `${purchaseModuleUrl}/?${query.toString()}#purchase`;
};

const buildExternalInventoryUrl = () => {
  const inventoryModuleUrl = getExternalInventoryBaseUrl();
  if (!inventoryModuleUrl || !isExternalInventoryNavEnabled()) return "";

  let theme = "dark";
  let token = "";
  if (typeof window !== "undefined") {
    theme = String(localStorage.getItem("dw-theme") || "dark").trim().toLowerCase() || "dark";
    token = String(localStorage.getItem("dw-auth-token") || "").trim();
  }

  const query = new URLSearchParams();
  query.set("token", token);
  query.set("theme", theme);
  return `${inventoryModuleUrl}/?${query.toString()}#inventory`;
};

const isModuleReachable = async moduleUrl => {
  if (!moduleUrl) return false;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 1500);
  try {
    await fetch(`${moduleUrl}/`, {
      method: "GET",
      mode: "no-cors",
      signal: controller.signal
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

const isPurchaseModuleReachable = async purchaseModuleUrl => isModuleReachable(purchaseModuleUrl);
const isInventoryModuleReachable = async inventoryModuleUrl => isModuleReachable(inventoryModuleUrl);

const ICON_PATHS = {
  dashboard: "M3 11.5A2.5 2.5 0 0 1 5.5 9H9V3h6v6h3.5A2.5 2.5 0 0 1 21 11.5v7A2.5 2.5 0 0 1 18.5 21h-13A2.5 2.5 0 0 1 3 18.5v-7z",
  appointments: "M7 3v2M17 3v2M4 8h16M6.5 5h11A2.5 2.5 0 0 1 20 7.5v11A2.5 2.5 0 0 1 17.5 21h-11A2.5 2.5 0 0 1 4 18.5v-11A2.5 2.5 0 0 1 6.5 5z",
  clients: "M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8zM5 20a7 7 0 0 1 14 0",
  whatsapp: "M12 3C7 3 3 6.7 3 11.2c0 2.3 1.1 4.3 2.8 5.7L5 21l4.2-1.3c.9.3 1.8.5 2.8.5 5 0 9-3.7 9-8.2S17 3 12 3zm4.2 11.8c-.2.5-1 .9-1.5 1-.4.1-.9.1-1.5-.1-.4-.1-.9-.3-1.6-.6-2.8-1.2-4.7-4.2-4.9-4.4-.2-.2-1.1-1.4-1.1-2.7 0-1.3.7-1.9 1-2.2.2-.2.4-.3.6-.3h.4c.1 0 .3 0 .4.4.2.5.7 1.8.8 1.9.1.1.1.3 0 .4-.1.1-.2.3-.4.4l-.3.3c-.1.1-.2.2-.1.4.1.2.5.9 1 1.4.7.8 1.4 1.1 1.6 1.2.2.1.3 0 .4-.1.1-.1.6-.7.8-.9.2-.2.3-.2.5-.1.2.1 1.5.7 1.7.8.2.1.4.2.4.3 0 .2 0 .8-.2 1.3z",
  staff: "M8 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6zM16 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6zM3 20a5 5 0 0 1 10 0M11 20a5 5 0 0 1 10 0",
  services: "M4 7h16M7 4v6M17 4v6M6 12h12v8H6z",
  branches: "M12 3l8 4v6c0 5-3.4 6.9-8 8c-4.6-1.1-8-3-8-8V7l8-4z",
  inventory: "M4 7l8-4l8 4v10l-8 4l-8-4V7zM4 7l8 4l8-4M12 11v10",
  purchase: "M5 4h10l4 4v12H5V4zM15 4v4h4M8 12h8M8 16h8",
  sales: "M4 19h16M7 16V9M12 16V6M17 16V11",
  reports: "M6 3h9l5 5v13H6V3zM15 3v5h5M9 13h6M9 17h6",
  settings: "M12 8.5a3.5 3.5 0 1 0 0 7a3.5 3.5 0 0 0 0-7zM19.4 15l1.1 1.9l-2 3.5l-2.2-.6a7.4 7.4 0 0 1-1.7 1l-.3 2.2H9.7l-.3-2.2a7.4 7.4 0 0 1-1.7-1l-2.2.6l-2-3.5L4.6 15a7.6 7.6 0 0 1 0-2l-1.1-1.9l2-3.5l2.2.6a7.4 7.4 0 0 1 1.7-1L9.7 5h4.6l.3 2.2a7.4 7.4 0 0 1 1.7 1l2.2-.6l2 3.5L19.4 13a7.6 7.6 0 0 1 0 2z",
  users: "M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8zM3 20a9 9 0 0 1 18 0"
};

const Sidebar = ({ availableMenus = [], isOpen = false, onNavigate }) => {
  const handleNavClick = async (event, key) => {
    onNavigate?.();

    if (PURCHASE_MENU_KEYS.has(key)) {
      const purchaseModuleUrl = getExternalPurchaseBaseUrl();
      const externalUrl = buildExternalPurchaseUrl();
      if (!purchaseModuleUrl || !externalUrl || !isExternalPurchaseNavEnabled()) return;

      event.preventDefault();
      const isReachable = await isPurchaseModuleReachable(purchaseModuleUrl);
      if (isReachable) {
        window.location.assign(externalUrl);
        return;
      }
      window.location.hash = "purchase";
      return;
    }

    if (INVENTORY_MENU_KEYS.has(key)) {
      const inventoryModuleUrl = getExternalInventoryBaseUrl();
      const externalUrl = buildExternalInventoryUrl();
      if (!inventoryModuleUrl || !externalUrl || !isExternalInventoryNavEnabled()) return;

      event.preventDefault();
      const isReachable = await isInventoryModuleReachable(inventoryModuleUrl);
      if (isReachable) {
        window.location.assign(externalUrl);
        return;
      }
      window.location.hash = "inventory";
      return;
    }
  };

  return (
  <nav className={`sidebar ${isOpen ? "open" : ""}`.trim()}>
    <ul className="sidebar-menu">
      {MENU_ITEMS.filter(item => availableMenus.includes(item.key)).map(item => (
        <li key={item.key} className="sidebar-item">
          <a href={getMenuHref(item.key)} className="sidebar-link" onClick={event => handleNavClick(event, item.key)}>
            <span className="sidebar-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d={ICON_PATHS[item.key] || ICON_PATHS.dashboard} />
              </svg>
            </span>
            <span>{item.label}</span>
          </a>
        </li>
      ))}
    </ul>
  </nav>
  );
};

export default Sidebar;
